import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import VideoPlayer from '../components/VideoPlayer';
import { movieAPI, userAPI } from '../services/api';
import { useUserStore } from '../stores/movieStore';
import type { Movie } from '../stores/movieStore';

const PlayerPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSourceId, setCurrentSourceId] = useState<string>('');
  const { addToHistory, updateHistoryProgress } = useUserStore();

  useEffect(() => {
    if (id) fetchMovieDetail(id);
  }, [id]);

  useEffect(() => {
    const sourceId = searchParams.get('source');
    if (sourceId && movie) {
      setCurrentSourceId(sourceId);
    } else if (movie && movie.sources.length > 0) {
      setCurrentSourceId(movie.sources[0].id);
    }
  }, [searchParams, movie]);

  const fetchMovieDetail = async (movieId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await movieAPI.getDetail(movieId);
      const movieData = response.data;
      if (!movieData) {
        setError('电影不存在');
        return;
      }
      setMovie(movieData);

      if (movieData.sources.length > 0) {
        const sourceId = searchParams.get('source') || movieData.sources[0].id;
        setCurrentSourceId(sourceId);
        addToHistory({
          videoId: movieData.id,
          source: movieData.sourceId || 'unknown',
          title: movieData.title,
          poster: movieData.poster,
          year: movieData.year,
          rating: movieData.rating,
          genre: movieData.genre,
          description: movieData.description,
          sourceId,
          progress: 0,
        });
        try {
          await userAPI.updateProgress(movieData.id, 0, sourceId, movieData);
        } catch {}
      }
    } catch {
      setError('加载失败，请刷新重试');
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = async (sourceId: string) => {
    setCurrentSourceId(sourceId);
    setSearchParams({ source: sourceId });

    if (movie) {
      addToHistory({
        videoId: movie.id,
        source: movie.sourceId || 'unknown',
        title: movie.title,
        poster: movie.poster,
        year: movie.year,
        rating: movie.rating,
        genre: movie.genre,
        description: movie.description,
        sourceId,
        progress: 0,
      });
      try {
        await userAPI.updateProgress(movie.id, 0, sourceId, movie);
      } catch {}
    }
  };

  const handleProgress = (progress: number) => {
    if (movie && currentSourceId) {
      updateHistoryProgress(movie.id, movie.sourceId || 'unknown', progress);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{error || '电影不存在'}</h2>
          <Link to="/" className="text-blue-400 hover:text-blue-300">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <VideoPlayer
          sources={movie.sources}
          currentSourceId={currentSourceId}
          onSourceChange={handleSourceChange}
          onProgress={handleProgress}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-white mb-2">{movie.title}</h1>
                {movie.originalTitle && <p className="text-gray-400">{movie.originalTitle}</p>}
              </div>
              <Link to={`/movie/${movie.id}`} className="px-4 py-2 bg-slate-800 text-gray-300 rounded-lg hover:bg-slate-700 transition-colors">
                查看详情
              </Link>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full">{movie.year}</span>
              <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-sm rounded-full">评分 {movie.rating.toFixed(1)}</span>
              {movie.genre.map((g) => (
                <span key={g} className="px-3 py-1 bg-slate-700/50 text-gray-300 text-sm rounded-full">{g}</span>
              ))}
            </div>

            <div className="p-4 bg-slate-800/50 rounded-xl">
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-400">当前播放:</span>
                <span className="text-white font-medium">
                  {movie.sources.find((s) => s.id === currentSourceId)?.name || '未知'}
                </span>
                <span className="text-gray-500">·</span>
                <span className="text-gray-400">
                  {movie.sources.find((s) => s.id === currentSourceId)?.quality || '未知'}
                </span>
              </div>
            </div>
          </div>

          <div className="lg:w-80">
            <div className="bg-slate-800/50 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-white mb-4">播放列表</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {movie.sources.map((source) => (
                  <button
                    key={source.id}
                    onClick={() => handleSourceChange(source.id)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      source.id === currentSourceId
                        ? 'bg-blue-500/20 border border-blue-500'
                        : 'bg-slate-700/50 hover:bg-slate-600/50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium">{source.name}</div>
                        <div className="text-gray-400 text-sm">{source.quality} · {source.language}</div>
                      </div>
                      {source.id === currentSourceId && (
                        <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 bg-slate-800/50 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-white mb-4">剧情简介</h3>
              <p className="text-gray-400 text-sm leading-relaxed line-clamp-4">{movie.description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerPage;
